import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  X,
  Lock,
  Save,
  RotateCcw,
  Upload,
  Plus,
  Trash2,
  MapPin,
  Image as ImageIcon,
  DollarSign,
  LineChart as LineChartIcon,
  ImagePlus,
  ArrowUp,
  ArrowDown,
  Monitor,
  Tablet,
  Smartphone,
  Users,
  Pencil,
  Check,
  FileText,
  Eye,
  EyeOff,
  Star,
  Calendar as CalendarIcon,
  Settings,
  Building2,
} from "lucide-react";
import LocationsMap from "./LocationsMap";
import RichTextEditor from "./RichTextEditor";
import MediaUploader, { CoverImagePreview } from "./MediaUploader";
import MediaLibrary from "./MediaLibrary";
import ClientsManager from "./ClientsManager";
import { useSettings } from "../lib/settings";
import { snapToHalf, isValidHalfStep, formatPrice } from "../lib/price";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "prices", label: "Prices", icon: DollarSign },
  { id: "history", label: "Graph", icon: LineChartIcon },
  { id: "branches", label: "Branches", icon: MapPin },
  { id: "logo", label: "Logo", icon: ImageIcon },
  { id: "gallery", label: "Gallery", icon: ImagePlus },
  { id: "team", label: "Team", icon: Users },
  { id: "blog", label: "Blog", icon: FileText },
  { id: "clients", label: "Clients", icon: Building2 },
  { id: "preview", label: "Preview", icon: Monitor },
];

const PREVIEW_DEVICES = [
  { id: "desktop", label: "Desktop", icon: Monitor, width: "100%", height: "100%" },
  { id: "tablet", label: "Tablet", icon: Tablet, width: "820px", height: "1100px" },
  { id: "mobile", label: "Mobile", icon: Smartphone, width: "390px", height: "780px" },
];

export const AdminPanel = ({ open, onClose, onChange }) => {
  const { logoUrl, refresh: refreshSettings } = useSettings();
  const [step, setStep] = useState("login"); // login | edit
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(localStorage.getItem("bst_admin_token") || "");
  const [tab, setTab] = useState("prices");
  const [loading, setLoading] = useState(false);

  // Section state
  const [prices, setPrices] = useState({});
  const [branches, setBranches] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState("ALL");
  const [logoPreview, setLogoPreview] = useState(null);
  const fileRef = useRef(null);

  // Gallery state
  const [gallery, setGallery] = useState([]);
  const galleryFileRef = useRef(null);

  // Team state
  const [team, setTeam] = useState([]);
  const [editMember, setEditMember] = useState(null); // { id?, name, role, bio, initials, image_url }
  const teamFileRef = useRef(null);

  // Blog state
  const [blogs, setBlogs] = useState([]);
  const [blogCategories, setBlogCategories] = useState([]);
  const [rawCategories, setRawCategories] = useState([]); // [{id, name, position}]
  const [editBlog, setEditBlog] = useState(null); // null | { id?, title, ... }
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);

  // Preview state
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [previewPath, setPreviewPath] = useState("/");

  const bootstrap = async (t) => {
    try {
      const [p, b, h, g, tm, bl, bc] = await Promise.all([
        axios.get(`${API}/admin/prices`, { headers: { "x-admin-token": t } }),
        axios.get(`${API}/admin/branches`, { headers: { "x-admin-token": t } }),
        axios.get(`${API}/admin/history`, { headers: { "x-admin-token": t } }),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/team`),
        axios.get(`${API}/admin/blogs`, { headers: { "x-admin-token": t } }),
        axios.get(`${API}/admin/blog/categories`, { headers: { "x-admin-token": t } }),
      ]);
      setPrices(p.data);
      setBranches(b.data);
      setHistory(h.data);
      setGallery(g.data || []);
      setTeam(tm.data || []);
      setBlogs(bl.data || []);
      setRawCategories(bc.data || []);
      setBlogCategories((bc.data || []).map((c) => c.name));
      setStep("edit");
    } catch {
      localStorage.removeItem("bst_admin_token");
      setToken("");
      setStep("login");
    }
  };

  // Bootstrap when modal opens with a token; reset when it closes.
  // Implemented via a setTimeout to satisfy strict set-state-in-effect lint.
  useEffect(() => {
    const t = setTimeout(() => {
      if (open && token) {
        bootstrap(token);
      } else if (!open) {
        setStep("login");
        setPassword("");
        setLogoPreview(null);
      }
    }, 0);
    return () => clearTimeout(t);
  }, [open]);

  const authenticate = async (pw) => {
    if (!pw) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pw });
      const t = res.data.token;
      setToken(t);
      localStorage.setItem("bst_admin_token", t);
      toast.success("Admin access granted");
      await bootstrap(t);
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const headers = { "x-admin-token": token };

  // ---- Prices ----
  const savePrices = async () => {
    setLoading(true);
    try {
      const cleaned = {};
      const invalid = [];
      for (const [k, v] of Object.entries(prices)) {
        if (v === "" || v === null || v === undefined) continue;
        if (!isValidHalfStep(v)) {
          invalid.push(`${k} = ${v}`);
          continue;
        }
        cleaned[k] = snapToHalf(v);
      }
      if (invalid.length) {
        toast.error(`Prices must be entered in 0.5 increments only — fix: ${invalid.join(", ")}`);
        setLoading(false);
        return;
      }
      const res = await axios.put(`${API}/admin/prices`, { prices: cleaned }, { headers });
      setPrices(res.data);
      toast.success("Prices saved — live across the site");
      onChange?.();
    } catch {
      toast.error("Failed to save prices");
    } finally {
      setLoading(false);
    }
  };

  const snapAllPrices = () => {
    const snapped = {};
    for (const [k, v] of Object.entries(prices)) {
      snapped[k] = snapToHalf(v);
    }
    setPrices(snapped);
    toast.info("Snapped all values to the nearest 0.5");
  };

  const resetPrices = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/prices/reset`, {}, { headers });
      setPrices(res.data);
      toast.success("Prices reset to defaults");
      onChange?.();
    } catch {
      toast.error("Failed to reset");
    } finally {
      setLoading(false);
    }
  };

  // ---- Branches ----
  const setBranchCoord = (name, lat, lng) =>
    setBranches((arr) =>
      arr.map((b) => (b.name === name ? { ...b, lat, lng } : b))
    );

  const saveBranches = async () => {
    setLoading(true);
    try {
      const res = await axios.put(
        `${API}/admin/branches`,
        { branches: branches.map((b) => ({ name: b.name, lat: Number(b.lat), lng: Number(b.lng) })) },
        { headers }
      );
      setBranches(res.data);
      toast.success("Branch coordinates saved");
      refreshSettings();
      onChange?.();
    } catch {
      toast.error("Failed to save branches");
    } finally {
      setLoading(false);
    }
  };

  // ---- History ----
  const reloadHistory = async () => {
    const h = await axios.get(`${API}/admin/history`, { headers });
    setHistory(h.data);
  };

  const addHistory = async () => {
    const brand = historyFilter !== "ALL" ? historyFilter : Object.keys(prices)[0] || "TATA";
    const today = new Date().toISOString().slice(0, 10);
    const seedPrice = snapToHalf(prices[brand] ?? 80);
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/history`,
        { brand, date: today, price: seedPrice },
        { headers }
      );
      await reloadHistory();
      toast.success("Entry added");
      onChange?.();
    } catch {
      toast.error("Failed to add");
    } finally {
      setLoading(false);
    }
  };

  const updateHistory = async (item, patch) => {
    // Snap price on update to enforce 0.5 increments
    if (patch.price !== undefined) {
      patch = { ...patch, price: snapToHalf(patch.price) };
    }
    const merged = { brand: item.brand, date: item.date, price: item.price, ...patch };
    setHistory((arr) => arr.map((x) => (x.id === item.id ? { ...x, ...patch } : x)));
    try {
      await axios.put(`${API}/admin/history/${item.id}`, {
        brand: merged.brand,
        date: merged.date,
        price: Number(merged.price),
      }, { headers });
      onChange?.();
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteHistory = async (id) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/history/${id}`, { headers });
      await reloadHistory();
      toast.success("Deleted");
      onChange?.();
    } catch {
      toast.error("Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const resetHistory = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/history/reset`, {}, { headers });
      await reloadHistory();
      toast.success("History reset");
      onChange?.();
    } catch {
      toast.error("Failed to reset");
    } finally {
      setLoading(false);
    }
  };

  // ---- Logo ----
  const onPickFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview({ file, dataUrl: e.target.result });
    reader.readAsDataURL(file);
  };

  const uploadLogo = async () => {
    if (!logoPreview?.file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", logoPreview.file);
      await axios.post(`${API}/admin/logo`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setLogoPreview(null);
      toast.success("Logo updated everywhere");
      refreshSettings();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const resetLogo = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/logo/reset`, {}, { headers });
      toast.success("Logo reset to default");
      refreshSettings();
      onChange?.();
    } catch {
      toast.error("Failed to reset logo");
    } finally {
      setLoading(false);
    }
  };

  // ---- Gallery ----
  const reloadGallery = async () => {
    const g = await axios.get(`${API}/gallery`);
    setGallery(g.data || []);
  };

  const uploadGallery = async (files) => {
    if (!files || files.length === 0) return;
    setLoading(true);
    try {
      const fd = new FormData();
      for (const f of files) {
        if (f && f.type.startsWith("image/")) fd.append("files", f);
      }
      await axios.post(`${API}/admin/gallery/upload`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      await reloadGallery();
      toast.success(`Uploaded ${files.length} ${files.length === 1 ? "image" : "images"}`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const deleteGalleryItem = async (id) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { headers });
      await reloadGallery();
      toast.success("Image removed");
      onChange?.();
    } catch {
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const reorderGallery = async (idx, dir) => {
    const next = [...gallery];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setGallery(next);
    try {
      await axios.put(
        `${API}/admin/gallery/reorder`,
        { ids: next.map((g) => g.id) },
        { headers }
      );
      onChange?.();
    } catch {
      toast.error("Reorder failed");
      reloadGallery();
    }
  };

  const resetGallery = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/gallery/reset`, {}, { headers });
      await reloadGallery();
      toast.success("Gallery reset to defaults");
      onChange?.();
    } catch {
      toast.error("Reset failed");
    } finally {
      setLoading(false);
    }
  };

  // ---- Team ----
  const reloadTeam = async () => {
    const t = await axios.get(`${API}/team`);
    setTeam(t.data || []);
  };

  const openNewMember = () =>
    setEditMember({ id: null, name: "", role: "", bio: "", full_bio: "", initials: "", image_url: "" });

  const saveMember = async () => {
    if (!editMember) return;
    if (!editMember.name.trim() || !editMember.role.trim()) {
      toast.error("Name and role are required");
      return;
    }
    setLoading(true);
    try {
      const body = {
        name: editMember.name.trim(),
        role: editMember.role.trim(),
        bio: editMember.bio.trim(),
        full_bio: (editMember.full_bio || "").trim(),
        initials: editMember.initials.trim(),
        image_url: editMember.image_url || "",
      };
      if (editMember.id) {
        await axios.put(`${API}/admin/team/${editMember.id}`, body, { headers });
        toast.success("Member updated");
      } else {
        await axios.post(`${API}/admin/team`, body, { headers });
        toast.success("Member added");
      }
      setEditMember(null);
      await reloadTeam();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const deleteMember = async (id) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/team/${id}`, { headers });
      await reloadTeam();
      toast.success("Member removed");
      onChange?.();
    } catch {
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const reorderTeam = async (idx, dir) => {
    const next = [...team];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setTeam(next);
    try {
      await axios.put(
        `${API}/admin/team/reorder`,
        { ids: next.map((m) => m.id) },
        { headers }
      );
      onChange?.();
    } catch {
      toast.error("Reorder failed");
      reloadTeam();
    }
  };

  const uploadMemberImage = async (memberId, file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please choose an image");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/team/upload-image/${memberId}`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      // Update local state
      setTeam((arr) => arr.map((m) => (m.id === memberId ? { ...m, image_url: res.data.image_url } : m)));
      if (editMember && editMember.id === memberId) {
        setEditMember({ ...editMember, image_url: res.data.image_url });
      }
      toast.success("Photo updated");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  // ---- Blog ----
  const emptyBlog = () => ({
    id: null,
    title: "",
    excerpt: "",
    content: "",
    cover_image_url: "",
    cover_image_id: null,
    category: blogCategories[0] || "Steel News",
    featured: false,
    status: "draft",
    seo_title: "",
    meta_description: "",
    slug: "",
    scheduled_at: "",
  });

  const reloadBlogs = async () => {
    const r = await axios.get(`${API}/admin/blogs`, { headers });
    setBlogs(r.data || []);
  };

  const reloadCategories = async () => {
    const r = await axios.get(`${API}/admin/blog/categories`, { headers });
    const list = (r.data || []).map((c) => c.name);
    setBlogCategories(list);
    setRawCategories(r.data || []);
  };

  const saveBlog = async () => {
    if (!editBlog) return;
    if (!editBlog.title.trim()) {
      toast.error("Title is required");
      return;
    }
    // Validate schedule
    let bodyStatus = editBlog.status;
    let scheduledAt = null;
    if (bodyStatus === "scheduled") {
      if (!editBlog.scheduled_at) {
        toast.error("Pick a future date & time to schedule");
        return;
      }
      const dt = new Date(editBlog.scheduled_at);
      if (Number.isNaN(dt.getTime())) {
        toast.error("Invalid schedule date");
        return;
      }
      if (dt <= new Date()) {
        toast.error("Schedule must be in the future");
        return;
      }
      scheduledAt = dt.toISOString();
    } else if (!["draft", "published"].includes(bodyStatus)) {
      bodyStatus = "draft";
    }

    setLoading(true);
    try {
      const body = {
        title: editBlog.title.trim(),
        excerpt: editBlog.excerpt.trim(),
        content: editBlog.content,
        cover_image_url: editBlog.cover_image_url.trim(),
        cover_image_id: editBlog.cover_image_id || null,
        category: editBlog.category,
        featured: !!editBlog.featured,
        status: bodyStatus,
        scheduled_at: scheduledAt,
        seo_title: editBlog.seo_title.trim(),
        meta_description: editBlog.meta_description.trim(),
        slug: editBlog.slug.trim(),
      };
      if (editBlog.id) {
        await axios.put(`${API}/admin/blogs/${editBlog.id}`, body, { headers });
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/admin/blogs`, body, { headers });
        toast.success("Article created");
      }
      setEditBlog(null);
      await reloadBlogs();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const togglePublish = async (blog) => {
    try {
      await axios.post(`${API}/admin/blogs/${blog.id}/publish`, {}, { headers });
      await reloadBlogs();
      toast.success(blog.status === "published" ? "Unpublished" : "Published");
      onChange?.();
    } catch {
      toast.error("Status change failed");
    }
  };

  const deleteBlog = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/blogs/${id}`, { headers });
      await reloadBlogs();
      toast.success("Article deleted");
      onChange?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  // ---- Blog categories ----
  const createCategory = async () => {
    const name = (newCategoryName || "").trim();
    if (!name) return;
    try {
      await axios.post(`${API}/admin/blog/categories`, { name }, { headers });
      setNewCategoryName("");
      await reloadCategories();
      toast.success(`Category "${name}" added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  const renameCategory = async (cat, newName) => {
    const name = (newName || "").trim();
    if (!name || name === cat.name) {
      setEditCategoryId(null);
      return;
    }
    try {
      await axios.put(
        `${API}/admin/blog/categories/${cat.id}`,
        { name },
        { headers }
      );
      setEditCategoryId(null);
      await reloadCategories();
      await reloadBlogs();
      toast.success("Category renamed");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}" category?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/categories/${cat.id}`, { headers });
      await reloadCategories();
      toast.success("Category deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (!open) return null;

  const filteredHistory = history.filter((h) =>
    historyFilter === "ALL" ? true : h.brand === historyFilter
  );

  const device = PREVIEW_DEVICES.find((d) => d.id === previewDevice);

  return (
    <div
      data-testid="admin-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[#1c1c1e]/60 p-2 backdrop-blur md:p-4"
      onClick={onClose}
    >
      <Toaster position="top-right" theme="light" />
      <div
        className="relative max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-black/5 bg-[#f7f7f5] px-5 py-3.5 md:px-6 md:py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#e85a1c] text-white">
              <Lock size={15} />
            </span>
            <div>
              <div className="font-display-med text-base uppercase tracking-tight text-[#1c1c1e]">
                Admin Panel
              </div>
              <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                Centralized site control
              </div>
            </div>
          </div>
          <button
            data-testid="admin-close"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#4a4a4e] hover:bg-black/5"
          >
            <X size={16} />
          </button>
        </div>

        {step === "login" ? (
          <div className="space-y-5 p-6">
            <div>
              <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                Password
              </label>
              <input
                data-testid="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && password) authenticate(password);
                }}
                className="mt-2 w-full rounded-lg border border-black/10 bg-white px-4 py-3 text-sm focus:border-[#e85a1c] focus:outline-none"
                placeholder="Enter admin password"
                autoFocus
              />
            </div>
            <button
              type="button"
              onClick={() => authenticate(password)}
              data-testid="admin-login-submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full bg-[#e85a1c] px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-all hover:bg-[#c64812] disabled:opacity-60"
            >
              {loading ? "Verifying…" : "Unlock Panel →"}
            </button>
          </div>
        ) : (
          <div className="flex max-h-[calc(92vh-64px)] flex-col md:flex-row">
            {/* Sidebar */}
            <aside className="shrink-0 border-b border-black/5 bg-[#f7f7f5] p-3 md:w-56 md:border-b-0 md:border-r md:p-4">
              <div className="hidden md:block">
                <div className="px-3 py-2 font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                  Dashboard
                </div>
              </div>
              <nav className="flex gap-2 overflow-x-auto md:flex-col md:gap-1.5 md:overflow-visible">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    data-testid={`admin-tab-${t.id}`}
                    className={`group inline-flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2.5 text-left text-[12px] font-semibold transition-all md:w-full ${
                      tab === t.id
                        ? "bg-[#e85a1c] text-white shadow-orange"
                        : "text-[#4a4a4e] hover:bg-white hover:text-[#1c1c1e]"
                    }`}
                  >
                    <span
                      className={`flex h-7 w-7 items-center justify-center rounded-md ${
                        tab === t.id
                          ? "bg-white/20 text-white"
                          : "bg-white text-[#e85a1c] shadow-lux"
                      }`}
                    >
                      <t.icon size={14} />
                    </span>
                    <span className="hidden md:inline">{t.label}</span>
                  </button>
                ))}
              </nav>

              <div className="mt-4 hidden border-t border-black/5 pt-4 md:block">
                <button
                  onClick={() => {
                    localStorage.removeItem("bst_admin_token");
                    setToken("");
                    setStep("login");
                    toast.info("Logged out");
                  }}
                  data-testid="admin-logout"
                  className="w-full rounded-lg px-3 py-2.5 text-left text-[11px] uppercase tracking-[0.18em] text-[#8b8b8f] transition-all hover:bg-white hover:text-[#1c1c1e]"
                >
                  Logout
                </button>
                <div className="mt-2 px-3 font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#bababd]">
                  BST · admin v1
                </div>
              </div>
            </aside>

            <div className="grow overflow-y-auto p-5 md:p-6">
              {tab === "prices" && (
                <div data-testid="admin-prices-tab">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                      Edit live prices · ₹ / kg
                    </div>
                    <div className="rounded-full bg-[#fff8f3] px-3 py-1 font-mono-industrial text-[10px] uppercase tracking-[0.2em] text-[#e85a1c]">
                      Rule · 0.5 increments only
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {Object.entries(prices).map(([brand, price]) => {
                      const valid = price === "" || isValidHalfStep(price);
                      return (
                        <div
                          key={brand}
                          className={`flex items-center gap-3 rounded-lg border p-3 ${
                            valid
                              ? "border-black/5 bg-[#f7f7f5]"
                              : "border-red-300 bg-red-50"
                          }`}
                        >
                          <div className="flex-1 font-display-med text-sm uppercase tracking-tight text-[#1c1c1e]">
                            {brand}
                          </div>
                          <div
                            className={`flex items-center rounded-md border bg-white ${
                              valid ? "border-black/10" : "border-red-400"
                            }`}
                          >
                            <span className="pl-3 text-sm text-[#8b8b8f]">₹</span>
                            <input
                              data-testid={`admin-price-${brand.replace(/\s+/g, "-").toLowerCase()}`}
                              type="number"
                              step="0.5"
                              min="0"
                              value={price}
                              onChange={(e) =>
                                setPrices((p) => ({ ...p, [brand]: e.target.value }))
                              }
                              onBlur={(e) => {
                                // Auto-snap on blur for friendlier UX
                                const raw = e.target.value;
                                if (raw === "" || raw === null) return;
                                const snapped = snapToHalf(raw);
                                if (String(snapped) !== String(raw)) {
                                  setPrices((p) => ({ ...p, [brand]: snapped }));
                                }
                              }}
                              className="w-32 bg-transparent px-2 py-2 font-mono-industrial text-sm focus:outline-none"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {Object.entries(prices).some(([, v]) => v !== "" && !isValidHalfStep(v)) && (
                    <div
                      data-testid="admin-price-validation-error"
                      className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
                    >
                      Prices must be entered in 0.5 increments only.
                    </div>
                  )}
                  <div className="mt-5 flex flex-wrap gap-2">
                    <button
                      onClick={savePrices}
                      data-testid="admin-save"
                      disabled={loading}
                      className="inline-flex items-center gap-2 rounded-full bg-[#e85a1c] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#c64812]"
                    >
                      <Save size={13} /> Save
                    </button>
                    <button
                      onClick={snapAllPrices}
                      data-testid="admin-snap-half"
                      disabled={loading}
                      className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                    >
                      Snap to 0.5
                    </button>
                    <button
                      onClick={resetPrices}
                      data-testid="admin-reset"
                      disabled={loading}
                      className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                    >
                      <RotateCcw size={13} /> Reset
                    </button>
                  </div>
                </div>
              )}

              {tab === "history" && (
                <div data-testid="admin-history-tab">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                      Historical price entries
                    </div>
                    <select
                      value={historyFilter}
                      onChange={(e) => setHistoryFilter(e.target.value)}
                      className="ml-auto rounded-md border border-black/10 bg-white px-3 py-1.5 text-xs"
                      data-testid="admin-history-filter"
                    >
                      <option value="ALL">{`All brands (${history.length})`}</option>
                      {Object.keys(prices).map((b) => (
                        <option key={b} value={b}>{`${b} (${history.filter((h) => h.brand === b).length})`}</option>
                      ))}
                    </select>
                    <button onClick={addHistory} data-testid="admin-history-add" className="inline-flex items-center gap-1.5 rounded-full bg-[#e85a1c] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
                      <Plus size={12} /> Add
                    </button>
                    <button onClick={resetHistory} data-testid="admin-history-reset" className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]">
                      <RotateCcw size={12} /> Reset
                    </button>
                  </div>

                  <div className="mt-4 max-h-[440px] overflow-y-auto rounded-lg border border-black/5">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-[#f7f7f5] text-left text-[10px] uppercase tracking-[0.2em] text-[#8b8b8f]">
                        <tr>
                          <th className="px-3 py-2">Brand</th>
                          <th className="px-3 py-2">Date</th>
                          <th className="px-3 py-2">Price ₹</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredHistory.map((h) => (
                          <tr key={h.id} className="border-t border-black/5" data-testid={`history-row-${h.id}`}>
                            <td className="px-3 py-1.5">
                              <select
                                value={h.brand}
                                onChange={(e) => updateHistory(h, { brand: e.target.value })}
                                className="rounded-md border border-black/10 bg-white px-2 py-1.5 text-xs"
                              >
                                {Object.keys(prices).map((b) => (
                                  <option key={b} value={b}>{b}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-1.5">
                              <input
                                type="date"
                                value={h.date}
                                onChange={(e) => updateHistory(h, { date: e.target.value })}
                                className="rounded-md border border-black/10 bg-white px-2 py-1.5 font-mono-industrial text-xs"
                              />
                            </td>
                            <td className="px-3 py-1.5">
                              <input
                                type="number"
                                step="0.5"
                                min="0"
                                value={h.price}
                                onChange={(e) => updateHistory(h, { price: e.target.value })}
                                className="w-24 rounded-md border border-black/10 bg-white px-2 py-1.5 font-mono-industrial text-xs"
                              />
                            </td>
                            <td className="px-3 py-1.5">
                              <button
                                onClick={() => deleteHistory(h.id)}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[#8b8b8f] hover:bg-red-50 hover:text-red-600"
                                data-testid={`history-delete-${h.id}`}
                              >
                                <Trash2 size={13} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {tab === "branches" && (
                <div data-testid="admin-branches-tab">
                  <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                    Drag pins to relocate · or type coordinates
                  </div>
                  <div className="mt-3 grid gap-4 md:grid-cols-2">
                    <LocationsMap
                      adminMode
                      branchesOverride={branches}
                      onMove={(name, lat, lng) => setBranchCoord(name, lat, lng)}
                    />
                    <div className="space-y-3">
                      {branches.map((b, i) => (
                        <div key={b.name} className="rounded-lg border border-black/5 bg-[#f7f7f5] p-3" data-testid={`admin-branch-row-${i}`}>
                          <div className="font-display-med text-[12px] uppercase tracking-tight text-[#1c1c1e]">{b.name}</div>
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            <input
                              type="number"
                              step="0.0001"
                              value={b.lat}
                              onChange={(e) => setBranchCoord(b.name, Number(e.target.value), b.lng)}
                              className="rounded-md border border-black/10 bg-white px-2 py-1.5 font-mono-industrial text-xs"
                              placeholder="Lat"
                            />
                            <input
                              type="number"
                              step="0.0001"
                              value={b.lng}
                              onChange={(e) => setBranchCoord(b.name, b.lat, Number(e.target.value))}
                              className="rounded-md border border-black/10 bg-white px-2 py-1.5 font-mono-industrial text-xs"
                              placeholder="Lng"
                            />
                          </div>
                        </div>
                      ))}
                      <button onClick={saveBranches} data-testid="admin-branches-save" className="inline-flex items-center gap-2 rounded-full bg-[#e85a1c] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#c64812]">
                        <Save size={13} /> Save Coordinates
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {tab === "logo" && (
                <div data-testid="admin-logo-tab">
                  <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                    Site logo · updates navbar · footer · favicon
                  </div>
                  <div className="mt-4 grid gap-5 md:grid-cols-2">
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const f = e.dataTransfer.files?.[0];
                        if (f) onPickFile(f);
                      }}
                      onClick={() => fileRef.current?.click()}
                      className="cursor-pointer rounded-2xl border-2 border-dashed border-black/10 bg-[#f7f7f5] p-8 text-center transition-all hover:border-[#e85a1c] hover:bg-white"
                      data-testid="logo-dropzone"
                    >
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="logo-file-input"
                        onChange={(e) => onPickFile(e.target.files?.[0])}
                      />
                      <Upload size={28} className="mx-auto text-[#e85a1c]" />
                      <div className="mt-3 text-sm font-semibold text-[#1c1c1e]">
                        Drop image here or click to choose
                      </div>
                      <div className="mt-1 text-xs text-[#8b8b8f]">
                        PNG, JPG, WebP · up to 5 MB
                      </div>
                    </div>
                    <div className="rounded-2xl border border-black/5 bg-[#f7f7f5] p-6">
                      <div className="flex items-center justify-between">
                        <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                          {logoPreview ? "Preview" : "Current logo"}
                        </div>
                        <div className="font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#bababd]">
                          (Transparent canvas)
                        </div>
                      </div>
                      <div
                        className="mt-4 flex h-48 items-center justify-center rounded-xl border border-black/5"
                        style={{
                          backgroundImage:
                            "linear-gradient(45deg, #e9e9e6 25%, transparent 25%), linear-gradient(-45deg, #e9e9e6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e9e9e6 75%), linear-gradient(-45deg, transparent 75%, #e9e9e6 75%)",
                          backgroundSize: "16px 16px",
                          backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
                          backgroundColor: "#ffffff",
                        }}
                      >
                        <img
                          src={logoPreview?.dataUrl || logoUrl}
                          alt="Logo"
                          className="max-h-40 max-w-full object-contain"
                          data-testid="logo-preview"
                        />
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button onClick={uploadLogo} disabled={!logoPreview || loading} data-testid="logo-save" className="inline-flex items-center gap-2 rounded-full bg-[#e85a1c] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#c64812] disabled:opacity-50">
                          <Save size={13} /> {logoPreview ? "Upload" : "Save"}
                        </button>
                        <button onClick={resetLogo} data-testid="logo-reset" className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]">
                          <RotateCcw size={13} /> Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tab === "gallery" && (
                <div data-testid="admin-gallery-tab">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                        Gallery management · {gallery.length} images
                      </div>
                      <div className="mt-1 text-xs text-[#8b8b8f]">
                        Drag & drop multiple images · reorder with arrows · delete to remove
                      </div>
                    </div>
                    <button
                      onClick={resetGallery}
                      data-testid="gallery-reset"
                      className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                    >
                      <RotateCcw size={12} /> Reset to defaults
                    </button>
                  </div>

                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      uploadGallery(Array.from(e.dataTransfer.files || []));
                    }}
                    onClick={() => galleryFileRef.current?.click()}
                    className="mt-5 cursor-pointer rounded-2xl border-2 border-dashed border-black/10 bg-[#f7f7f5] p-8 text-center transition-all hover:border-[#e85a1c] hover:bg-white"
                    data-testid="gallery-dropzone"
                  >
                    <input
                      ref={galleryFileRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      data-testid="gallery-file-input"
                      onChange={(e) => {
                        uploadGallery(Array.from(e.target.files || []));
                        e.target.value = "";
                      }}
                    />
                    <Upload size={28} className="mx-auto text-[#e85a1c]" />
                    <div className="mt-3 text-sm font-semibold text-[#1c1c1e]">
                      Drop one or more images here, or click to choose
                    </div>
                    <div className="mt-1 text-xs text-[#8b8b8f]">
                      PNG, JPG, WebP · up to 5 MB each · multi-upload supported
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {gallery.map((g, idx) => (
                      <div
                        key={g.id}
                        className="group relative overflow-hidden rounded-xl border border-black/5 bg-[#f7f7f5] shadow-lux"
                        data-testid={`gallery-item-${idx}`}
                      >
                        <div className="aspect-[4/3] w-full overflow-hidden bg-black/5">
                          <img src={g.url} alt={`gallery ${idx + 1}`} className="h-full w-full object-cover" />
                        </div>
                        <div className="absolute right-1.5 top-1.5 flex items-center gap-1">
                          <button
                            onClick={() => reorderGallery(idx, -1)}
                            disabled={idx === 0}
                            data-testid={`gallery-up-${idx}`}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-[#1c1c1e] shadow-lux disabled:opacity-40 hover:bg-white"
                            title="Move up"
                          >
                            <ArrowUp size={12} />
                          </button>
                          <button
                            onClick={() => reorderGallery(idx, 1)}
                            disabled={idx === gallery.length - 1}
                            data-testid={`gallery-down-${idx}`}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-[#1c1c1e] shadow-lux disabled:opacity-40 hover:bg-white"
                            title="Move down"
                          >
                            <ArrowDown size={12} />
                          </button>
                          <button
                            onClick={() => deleteGalleryItem(g.id)}
                            data-testid={`gallery-delete-${idx}`}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-red-600 shadow-lux hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                        <div className="absolute left-1.5 top-1.5 rounded-full bg-black/65 px-2 py-0.5 font-mono-industrial text-[9px] uppercase tracking-[0.2em] text-white">
                          {String(idx + 1).padStart(2, "0")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tab === "team" && (
                <div data-testid="admin-team-tab">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                        Team management · {team.length} members
                      </div>
                      <div className="mt-1 text-xs text-[#8b8b8f]">
                        Add, edit, reorder, and upload profile photos for team members.
                      </div>
                    </div>
                    <button
                      onClick={openNewMember}
                      data-testid="team-add"
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#e85a1c] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white hover:bg-[#c64812]"
                    >
                      <Plus size={12} /> Add member
                    </button>
                  </div>

                  {/* Editor */}
                  {editMember && (
                    <div className="mt-5 rounded-2xl border border-[#e85a1c]/30 bg-[#fff8f3] p-5" data-testid="team-editor">
                      <div className="flex items-center justify-between">
                        <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                          {editMember.id ? "Edit member" : "New member"}
                        </div>
                        <button
                          onClick={() => setEditMember(null)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[#4a4a4e] hover:bg-black/5"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <div>
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">Name</label>
                          <input
                            value={editMember.name}
                            onChange={(e) => setEditMember({ ...editMember, name: e.target.value })}
                            data-testid="team-input-name"
                            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
                            placeholder="Full name"
                          />
                        </div>
                        <div>
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">Designation</label>
                          <input
                            value={editMember.role}
                            onChange={(e) => setEditMember({ ...editMember, role: e.target.value })}
                            data-testid="team-input-role"
                            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
                            placeholder="e.g. Managing Director"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">Short description · shown on card</label>
                          <textarea
                            value={editMember.bio}
                            onChange={(e) => setEditMember({ ...editMember, bio: e.target.value })}
                            data-testid="team-input-bio"
                            rows={2}
                            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
                            placeholder="One-line summary (visible on the card)"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">Expanded bio · shown in popup</label>
                          <textarea
                            value={editMember.full_bio || ""}
                            onChange={(e) => setEditMember({ ...editMember, full_bio: e.target.value })}
                            data-testid="team-input-full-bio"
                            rows={5}
                            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm leading-relaxed focus:border-[#e85a1c] focus:outline-none"
                            placeholder="Full leadership profile — responsibilities, role overview, contribution. Shown in the modal when a user clicks the team card. Use blank lines to separate paragraphs."
                          />
                        </div>
                        <div>
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">Initials (optional)</label>
                          <input
                            value={editMember.initials}
                            onChange={(e) => setEditMember({ ...editMember, initials: e.target.value })}
                            data-testid="team-input-initials"
                            maxLength={3}
                            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm uppercase tracking-wider focus:border-[#e85a1c] focus:outline-none"
                            placeholder="Auto from name"
                          />
                        </div>
                        <div>
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">Profile photo</label>
                          <div className="mt-1 flex items-center gap-3">
                            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-black/10 bg-white">
                              {editMember.image_url ? (
                                <img src={editMember.image_url} alt="preview" className="h-full w-full object-cover" />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center text-[10px] text-[#bababd]">
                                  No photo
                                </div>
                              )}
                            </div>
                            <input
                              ref={teamFileRef}
                              type="file"
                              accept="image/*"
                              data-testid="team-input-image"
                              className="hidden"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (!f) return;
                                if (editMember.id) {
                                  uploadMemberImage(editMember.id, f);
                                } else {
                                  // For new members, read locally and stash dataURL
                                  const r = new FileReader();
                                  r.onload = (ev) => setEditMember({ ...editMember, image_url: ev.target.result });
                                  r.readAsDataURL(f);
                                }
                                e.target.value = "";
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => teamFileRef.current?.click()}
                              className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                            >
                              <Upload size={11} /> {editMember.image_url ? "Replace" : "Upload"}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 flex flex-wrap gap-2">
                        <button
                          onClick={saveMember}
                          disabled={loading}
                          data-testid="team-save"
                          className="inline-flex items-center gap-2 rounded-full bg-[#e85a1c] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#c64812] disabled:opacity-60"
                        >
                          <Check size={13} /> {editMember.id ? "Update" : "Create"}
                        </button>
                        <button
                          onClick={() => setEditMember(null)}
                          className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* List */}
                  <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {team.map((m, idx) => (
                      <div
                        key={m.id}
                        className="rounded-xl border border-black/5 bg-[#f7f7f5] p-4"
                        data-testid={`team-row-${idx}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-white">
                            {m.image_url ? (
                              <img src={m.image_url} alt={m.name} className="h-full w-full object-cover" />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center font-display text-xl text-[#e85a1c]">
                                {m.initials || m.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-display-med text-sm uppercase tracking-tight text-[#1c1c1e]">{m.name}</div>
                            <div className="truncate font-mono-industrial text-[10px] uppercase tracking-[0.2em] text-[#e85a1c]">{m.role}</div>
                          </div>
                        </div>
                        {m.bio && (
                          <p className="mt-3 line-clamp-3 text-xs leading-relaxed text-[#4a4a4e]">{m.bio}</p>
                        )}
                        <div className="mt-3 flex items-center justify-between gap-1">
                          <div className="flex gap-1">
                            <button
                              onClick={() => reorderTeam(idx, -1)}
                              disabled={idx === 0}
                              data-testid={`team-up-${idx}`}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/10 bg-white text-[#4a4a4e] disabled:opacity-40 hover:border-[#e85a1c] hover:text-[#e85a1c]"
                              title="Move up"
                            >
                              <ArrowUp size={11} />
                            </button>
                            <button
                              onClick={() => reorderTeam(idx, 1)}
                              disabled={idx === team.length - 1}
                              data-testid={`team-down-${idx}`}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/10 bg-white text-[#4a4a4e] disabled:opacity-40 hover:border-[#e85a1c] hover:text-[#e85a1c]"
                              title="Move down"
                            >
                              <ArrowDown size={11} />
                            </button>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setEditMember({ ...m })}
                              data-testid={`team-edit-${idx}`}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/10 bg-white text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                              title="Edit"
                            >
                              <Pencil size={11} />
                            </button>
                            <button
                              onClick={() => deleteMember(m.id)}
                              data-testid={`team-delete-${idx}`}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/10 bg-white text-red-600 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 size={11} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tab === "blog" && (
                <div data-testid="admin-blog-tab">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                        Blog management · {blogs.length} articles · {rawCategories.length} categories
                      </div>
                      <div className="mt-1 text-xs text-[#8b8b8f]">
                        Create, edit, publish, schedule, and manage articles for the journal.
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setShowMediaLibrary((v) => !v)}
                        data-testid="blog-toggle-media-library"
                        className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1c1c1e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                      >
                        <FileText size={12} /> {showMediaLibrary ? "Hide" : "Open"} media library
                      </button>
                      <button
                        onClick={() => setShowCategoryManager((v) => !v)}
                        data-testid="blog-toggle-categories"
                        className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1c1c1e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                      >
                        <Settings size={12} /> {showCategoryManager ? "Hide" : "Manage"} categories
                      </button>
                      <button
                        onClick={() => setEditBlog(emptyBlog())}
                        data-testid="blog-new"
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#e85a1c] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white hover:bg-[#c64812]"
                      >
                        <Plus size={12} /> New article
                      </button>
                    </div>
                  </div>

                  {/* Media Library panel */}
                  {showMediaLibrary && (
                    <div className="mt-5 rounded-2xl border border-black/10 bg-white p-5">
                      <MediaLibrary token={token} mode="panel" />
                    </div>
                  )}

                  {/* Category Manager */}
                  {showCategoryManager && (
                    <div
                      className="mt-5 rounded-2xl border border-black/10 bg-white p-5"
                      data-testid="blog-category-manager"
                    >
                      <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                        Blog categories
                      </div>
                      <div className="mt-1 text-xs text-[#8b8b8f]">
                        Add, rename, or remove categories. You cannot delete a category that&apos;s in use.
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        <input
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") createCategory(); }}
                          data-testid="blog-category-new-input"
                          placeholder="New category name"
                          className="min-w-[220px] flex-1 rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
                        />
                        <button
                          onClick={createCategory}
                          data-testid="blog-category-create"
                          className="inline-flex items-center gap-1.5 rounded-full bg-[#e85a1c] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white hover:bg-[#c64812]"
                        >
                          <Plus size={12} /> Add
                        </button>
                      </div>

                      <ul className="mt-4 space-y-2">
                        {rawCategories.map((cat) => (
                          <li
                            key={cat.id}
                            data-testid={`blog-category-row-${cat.id}`}
                            className="flex items-center gap-2 rounded-lg border border-black/5 bg-[#f7f7f5] px-3 py-2"
                          >
                            {editCategoryId === cat.id ? (
                              <input
                                defaultValue={cat.name}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") renameCategory(cat, e.currentTarget.value);
                                  if (e.key === "Escape") setEditCategoryId(null);
                                }}
                                onBlur={(e) => renameCategory(cat, e.currentTarget.value)}
                                data-testid={`blog-category-edit-input-${cat.id}`}
                                autoFocus
                                className="flex-1 rounded-md border border-[#e85a1c]/40 bg-white px-2 py-1 text-sm focus:border-[#e85a1c] focus:outline-none"
                              />
                            ) : (
                              <span
                                data-testid={`blog-category-name-${cat.id}`}
                                className="flex-1 text-sm text-[#1c1c1e]"
                              >
                                {cat.name}
                              </span>
                            )}
                            <button
                              onClick={() => setEditCategoryId(editCategoryId === cat.id ? null : cat.id)}
                              data-testid={`blog-category-rename-${cat.id}`}
                              title="Rename"
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/10 bg-white text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                            >
                              <Pencil size={11} />
                            </button>
                            <button
                              onClick={() => deleteCategory(cat)}
                              data-testid={`blog-category-delete-${cat.id}`}
                              title="Delete"
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/10 bg-white text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={11} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Editor */}
                  {editBlog && (
                    <div
                      className="mt-5 rounded-2xl border border-[#e85a1c]/30 bg-[#fff8f3] p-5"
                      data-testid="blog-editor"
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                          {editBlog.id ? "Edit article" : "New article"}
                        </div>
                        <button
                          onClick={() => setEditBlog(null)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[#4a4a4e] hover:bg-black/5"
                          aria-label="Close editor"
                        >
                          <X size={14} />
                        </button>
                      </div>

                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <div className="md:col-span-2">
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">Title *</label>
                          <input
                            value={editBlog.title}
                            onChange={(e) => setEditBlog({ ...editBlog, title: e.target.value })}
                            data-testid="blog-title"
                            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
                            placeholder="How to choose the right TMT bar"
                          />
                        </div>

                        <div>
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">Category</label>
                          <select
                            value={editBlog.category}
                            onChange={(e) => setEditBlog({ ...editBlog, category: e.target.value })}
                            data-testid="blog-category"
                            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
                          >
                            {blogCategories.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">Slug (URL)</label>
                          <input
                            value={editBlog.slug}
                            onChange={(e) => setEditBlog({ ...editBlog, slug: e.target.value })}
                            data-testid="blog-slug"
                            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 font-mono-industrial text-xs focus:border-[#e85a1c] focus:outline-none"
                            placeholder="auto-generated from title"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                            Cover image · 16:9 (auto-cropped to 1600 × 900)
                          </label>
                          <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-3">
                            <div className="md:col-span-2">
                              {editBlog.cover_image_url ? (
                                <CoverImagePreview
                                  url={editBlog.cover_image_url}
                                  onReplace={() => setShowMediaPicker(true)}
                                  onRemove={() =>
                                    setEditBlog({
                                      ...editBlog,
                                      cover_image_url: "",
                                      cover_image_id: null,
                                    })
                                  }
                                />
                              ) : (
                                <MediaUploader
                                  token={token}
                                  onUploaded={(m) =>
                                    setEditBlog({
                                      ...editBlog,
                                      cover_image_id: m.id,
                                      cover_image_url: m.urls?.large || m.url || "",
                                    })
                                  }
                                />
                              )}
                            </div>
                            <div className="flex flex-col gap-2">
                              <button
                                type="button"
                                onClick={() => setShowMediaPicker(true)}
                                data-testid="blog-cover-pick-library"
                                className="rounded-full border border-black/10 bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1c1c1e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                              >
                                Choose from library
                              </button>
                              <div className="rounded-lg bg-[#f7f7f5] p-2 font-mono-industrial text-[9px] uppercase tracking-[0.18em] text-[#8b8b8f]">
                                JPG · PNG · WEBP<br />Auto generates thumb · medium · large variants
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="md:col-span-2">
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">Excerpt · short summary card</label>
                          <textarea
                            value={editBlog.excerpt}
                            onChange={(e) => setEditBlog({ ...editBlog, excerpt: e.target.value })}
                            data-testid="blog-excerpt"
                            rows={2}
                            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
                            placeholder="A one-or-two sentence teaser."
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">Content · rich text editor</label>
                          <div className="mt-1">
                            <RichTextEditor
                              value={editBlog.content}
                              onChange={(html) => setEditBlog({ ...editBlog, content: html })}
                              placeholder="Start writing your article…"
                              token={token}
                            />
                          </div>
                          <div className="mt-1 font-mono-industrial text-[9px] uppercase tracking-[0.2em] text-[#bababd]">
                            Format with the toolbar · headings, lists, links &amp; images supported.
                          </div>
                        </div>

                        <div>
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">SEO Title</label>
                          <input
                            value={editBlog.seo_title}
                            onChange={(e) => setEditBlog({ ...editBlog, seo_title: e.target.value })}
                            data-testid="blog-seo-title"
                            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
                            placeholder="Defaults to article title"
                          />
                        </div>
                        <div>
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">Meta Description</label>
                          <input
                            value={editBlog.meta_description}
                            onChange={(e) => setEditBlog({ ...editBlog, meta_description: e.target.value })}
                            data-testid="blog-meta-desc"
                            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
                            placeholder="≤ 160 characters"
                          />
                        </div>

                        <div className="md:col-span-2 border-t border-black/5 pt-4">
                          <label className="inline-flex items-center gap-2 text-sm text-[#1c1c1e]">
                            <input
                              type="checkbox"
                              checked={!!editBlog.featured}
                              onChange={(e) => setEditBlog({ ...editBlog, featured: e.target.checked })}
                              data-testid="blog-featured-toggle"
                              className="h-4 w-4 accent-[#e85a1c]"
                            />
                            <Star size={13} className="text-[#e85a1c]" />
                            Mark as Featured
                          </label>
                        </div>

                        <div>
                          <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">Status</label>
                          <select
                            value={editBlog.status}
                            onChange={(e) =>
                              setEditBlog({
                                ...editBlog,
                                status: e.target.value,
                                scheduled_at:
                                  e.target.value === "scheduled"
                                    ? editBlog.scheduled_at || ""
                                    : "",
                              })
                            }
                            data-testid="blog-status"
                            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
                          >
                            <option value="draft">Save as Draft</option>
                            <option value="published">Publish immediately</option>
                            <option value="scheduled">Schedule for later</option>
                          </select>
                        </div>

                        {editBlog.status === "scheduled" && (
                          <div>
                            <label className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                              <CalendarIcon size={11} className="-mt-0.5 mr-1 inline" />
                              Publish on
                            </label>
                            <input
                              type="datetime-local"
                              value={editBlog.scheduled_at || ""}
                              onChange={(e) =>
                                setEditBlog({ ...editBlog, scheduled_at: e.target.value })
                              }
                              data-testid="blog-scheduled-at"
                              className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
                            />
                            <div className="mt-1 font-mono-industrial text-[9px] uppercase tracking-[0.2em] text-[#bababd]">
                              Article auto-publishes at this time.
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="mt-5 flex flex-wrap gap-2">
                        <button
                          onClick={saveBlog}
                          disabled={loading}
                          data-testid="blog-save"
                          className="inline-flex items-center gap-2 rounded-full bg-[#e85a1c] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#c64812] disabled:opacity-60"
                        >
                          <Check size={13} /> {editBlog.id ? "Update" : "Create"}
                        </button>
                        <button
                          onClick={() => setEditBlog(null)}
                          className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                        >
                          Cancel
                        </button>
                      </div>

                      {/* Media picker modal — used by Replace + Choose from library */}
                      {showMediaPicker && (
                        <MediaLibrary
                          token={token}
                          mode="picker"
                          selectedId={editBlog.cover_image_id}
                          onClose={() => setShowMediaPicker(false)}
                          onSelect={(m) => {
                            setEditBlog({
                              ...editBlog,
                              cover_image_id: m.id,
                              cover_image_url: m.urls?.large || m.url || "",
                            });
                            setShowMediaPicker(false);
                          }}
                        />
                      )}
                    </div>
                  )}

                  {/* List of blogs */}
                  <div className="mt-6 space-y-3">
                    {blogs.length === 0 && (
                      <div className="rounded-xl border border-dashed border-black/10 bg-[#f7f7f5] p-8 text-center text-sm text-[#8b8b8f]">
                        No articles yet — click &ldquo;New article&rdquo; to get started.
                      </div>
                    )}
                    {blogs.map((b) => (
                      <div
                        key={b.id}
                        data-testid={`blog-row-${b.id}`}
                        className="flex flex-wrap items-center gap-3 rounded-xl border border-black/5 bg-[#f7f7f5] p-3"
                      >
                        <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-white">
                          {b.cover_image_url ? (
                            <img
                              src={b.cover_image_url.startsWith("http") ? b.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${b.cover_image_url}`}
                              alt={b.title}
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-[10px] text-[#bababd]">
                              No cover
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#e85a1c]">
                              {b.category}
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono-industrial text-[9px] uppercase tracking-[0.18em] ${
                                b.status === "published"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : b.status === "scheduled"
                                  ? "bg-amber-50 text-amber-700"
                                  : "bg-black/5 text-[#8b8b8f]"
                              }`}
                              data-testid={`blog-status-badge-${b.id}`}
                            >
                              {b.status === "scheduled" && <CalendarIcon size={9} />}
                              {b.status === "scheduled" && b.scheduled_at
                                ? `Scheduled · ${new Date(b.scheduled_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`
                                : b.status}
                            </span>
                            {b.featured && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-[#fff8f3] px-2 py-0.5 font-mono-industrial text-[9px] uppercase tracking-[0.18em] text-[#e85a1c]">
                                <Star size={9} /> Featured
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 truncate font-display-med text-sm uppercase tracking-tight text-[#1c1c1e]">
                            {b.title}
                          </div>
                          <div className="truncate font-mono-industrial text-[10px] text-[#8b8b8f]">
                            /blog/{b.slug}
                          </div>
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <button
                            onClick={() => togglePublish(b)}
                            data-testid={`blog-publish-${b.id}`}
                            title={b.status === "published" ? "Unpublish" : "Publish"}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                          >
                            {b.status === "published" ? <EyeOff size={12} /> : <Eye size={12} />}
                          </button>
                          <button
                            onClick={() => setEditBlog({
                              ...b,
                              content: b.content || "",
                              cover_image_id: b.cover_image_id || null,
                              scheduled_at: b.scheduled_at
                                ? new Date(b.scheduled_at).toISOString().slice(0, 16)
                                : "",
                            })}
                            data-testid={`blog-edit-${b.id}`}
                            title="Edit"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            onClick={() => deleteBlog(b.id)}
                            data-testid={`blog-delete-${b.id}`}
                            title="Delete"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tab === "clients" && (
                <div data-testid="admin-clients-tab">
                  <ClientsManager token={token} onChange={onChange} />
                </div>
              )}

              {tab === "preview" && (
                <div data-testid="admin-preview-tab">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                        Responsive preview
                      </div>
                      <div className="mt-1 text-xs text-[#8b8b8f]">
                        Preview live changes across devices before publishing.
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={previewPath}
                        onChange={(e) => setPreviewPath(e.target.value)}
                        data-testid="preview-path-select"
                        className="rounded-md border border-black/10 bg-white px-3 py-1.5 text-xs"
                      >
                        <option value="/">Home</option>
                        <option value="/about/team">About · Team</option>
                        <option value="/about/timeline">About · Timeline</option>
                        <option value="/about/gallery">About · Gallery</option>
                        <option value="/price">Price</option>
                        <option value="/products">Products & Services</option>
                        <option value="/blog">Blog</option>
                      </select>
                      <div className="inline-flex rounded-full border border-black/10 bg-white p-1">
                        {PREVIEW_DEVICES.map((d) => (
                          <button
                            key={d.id}
                            onClick={() => setPreviewDevice(d.id)}
                            data-testid={`preview-${d.id}`}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-all ${
                              previewDevice === d.id
                                ? "bg-[#e85a1c] text-white"
                                : "text-[#4a4a4e] hover:text-[#1c1c1e]"
                            }`}
                          >
                            <d.icon size={12} /> {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex items-center justify-center overflow-auto rounded-2xl border border-black/5 bg-[#f7f7f5] p-4" style={{ minHeight: 540 }}>
                    <div
                      className="shrink-0 overflow-hidden rounded-xl border border-black/10 bg-white shadow-lux"
                      style={{
                        width: device.width,
                        height: device.height,
                        maxWidth: "100%",
                      }}
                      data-testid="preview-frame-wrap"
                    >
                      <iframe
                        key={previewDevice + previewPath}
                        src={previewPath}
                        title="Site preview"
                        className="h-full w-full"
                        style={{ border: 0 }}
                        data-testid="preview-iframe"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
